import type { FC } from 'react';

import { IconButton, Tooltip } from '@mui/material';
import { clsx } from 'clsx';
import { Link as RouterLink } from 'react-router-dom';

import { prefetchRoute } from 'app/routes/routePrefetch';
import { SIDEBAR_RAIL_WIDTH } from 'shared/lib/sidebar/useSidebarStore';
import InboxIcon from 'shared/ui/icons/inbox.svg?react';
import LogoTileIcon from 'shared/ui/icons/logo-tile.svg?react';

import type { Item, RenderItemsProps } from './types';
import { isItemHidden } from './utils/isItemHidden';
import { isRoutePart } from './utils/isRoutePart';

const tileClassName = 'h-8 w-8 shrink-0 rounded-lg p-0 text-white';

type NavRailProps = {
  items: Item[];
  tenantInitial?: string;
  isSandboxOn: boolean;
  isSandboxVisible: boolean;
} & Pick<RenderItemsProps, 'pathname' | 'permissions'>;

export const NavRail: FC<NavRailProps> = ({
  items,
  pathname,
  permissions,
  tenantInitial,
  isSandboxOn,
  isSandboxVisible,
}) => (
  <div
    className="flex h-full flex-col items-center bg-grey100"
    data-testid="nav-rail"
    style={{ width: SIDEBAR_RAIL_WIDTH }}
  >
    <RouterLink className="mt-[27px] flex h-8 w-8" to="/">
      <LogoTileIcon />
    </RouterLink>

    <div className="mt-[45px] flex w-full flex-col items-center gap-2 px-3">
      {items.map((item) => {
        if (isItemHidden(item, permissions)) {
          return null;
        }

        const isActive = isRoutePart(pathname, item.href);

        return (
          <Tooltip key={`${item.href}-${item.title}`} placement="right" title={item.title}>
            <IconButton
              aria-label={item.title}
              className={clsx(tileClassName, isActive ? 'bg-primary-main' : 'hover:bg-white/10')}
              component={item.href ? RouterLink : 'button'}
              onClick={item.customAction}
              onMouseEnter={() => item.href && prefetchRoute(item.href)}
              {...(item.href && { to: { pathname: item.href, state: { internal: true } } })}
            >
              {item.icon}
            </IconButton>
          </Tooltip>
        );
      })}
    </div>

    <div className="mt-auto flex w-full flex-col bg-white/10 backdrop-blur-[15px]">
      {tenantInitial && (
        <div className="flex h-[52px] items-center justify-center">
          <div className="flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-white/20 text-base leading-7 text-white">
            {tenantInitial}
          </div>
        </div>
      )}
      {isSandboxVisible && (
        <div className="flex h-[54px] items-center justify-center border-white/10 border-t-2">
          <Tooltip placement="right" title={`Sandbox mode is ${isSandboxOn ? 'on' : 'off'}`}>
            <div className={clsx(tileClassName, 'flex items-center justify-center')}>
              <InboxIcon />
            </div>
          </Tooltip>
        </div>
      )}
    </div>
  </div>
);
